import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Confetti from "react-confetti";
import { API } from "../App";
import { Button } from "../components/ui/button";
import {
  Flame,
  FileText,
  TrendingUp,
  CheckCircle,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Check,
  Apple,
  Beef,
  Wheat,
  Droplet,
  MessageSquare
} from "lucide-react";

// Simulated weekly meal plan data
const generateWeeklyPlan = () => {
  const today = new Date();
  const days = [];
  
  for (let i = -3; i <= 3; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    
    days.push({
      date: date,
      dayName: date.toLocaleDateString('es-ES', { weekday: 'short' }).charAt(0).toUpperCase(),
      dayNumber: date.getDate(),
      isToday: i === 0,
      meals: {
        desayuno: {
          name: "Desayuno",
          foods: ["Avena con frutas", "Huevos revueltos", "Jugo natural"],
          calories: 450,
          protein: 25,
          carbs: 55,
          fat: 15,
          completed: i < 0 || (i === 0 && Math.random() > 0.5),
          time: "07:00 - 09:00"
        },
        almuerzo: {
          name: "Almuerzo", 
          foods: ["Pollo a la plancha", "Arroz integral", "Ensalada mixta"],
          calories: 650,
          protein: 45,
          carbs: 60,
          fat: 20,
          completed: i < 0,
          time: "12:00 - 14:00"
        },
        cena: {
          name: "Cena",
          foods: ["Pescado al vapor", "Verduras salteadas", "Quinoa"],
          calories: 500,
          protein: 40,
          carbs: 35,
          fat: 18,
          completed: i < -1,
          time: "19:00 - 21:00"
        },
        snacks: {
          name: "Snacks",
          foods: ["Frutos secos", "Yogurt natural", "Fruta fresca"],
          calories: 200,
          protein: 10,
          carbs: 25,
          fat: 7,
          completed: i < 0,
          time: "Durante el día"
        }
      },
      dailyGoals: {
        calories: 1800,
        protein: 120,
        carbs: 175,
        fat: 60
      }
    });
  }
  return days;
};

// Simulated WhatsApp Q&A data
const simulatedQA = [
  { 
    id: 1, 
    question: "¿Puedo sustituir el arroz por quinoa?", 
    answer: "¡Claro que sí! La quinoa es una excelente alternativa. Tiene más proteína y fibra. Mantén la misma porción (1 taza cocida).",
    date: "Hace 2 días",
    category: "Sustituciones"
  },
  { 
    id: 2, 
    question: "¿Qué puedo comer si tengo antojo de algo dulce?", 
    answer: "Prueba con frutas naturales como fresas o mango. También puedes optar por un poco de chocolate oscuro (70% cacao o más). Lo importante es la moderación.",
    date: "Hace 5 días",
    category: "Antojos"
  },
  { 
    id: 3, 
    question: "¿Puedo saltarme el snack de la tarde?", 
    answer: "Los snacks te ayudan a mantener el metabolismo activo y evitar que llegues con mucha hambre a la cena. Te recomiendo mantenerlos, pero si no tienes hambre, puedes reducir la porción.",
    date: "Hace 1 semana",
    category: "Horarios"
  },
];

const CircularProgress = ({ value, max, color, size = 80, strokeWidth = 8, label, icon: Icon }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = Math.min(value / max, 1);
  const strokeDashoffset = circumference - progress * circumference;
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          {Icon && <Icon className="w-5 h-5" style={{ color }} />}
        </div>
      </div>
      <p className="mt-1 text-lg font-bold text-[#0F4D40]">{Math.round(max - value)}g</p>
      <p className="text-xs text-gray-500">{label} left</p>
    </div>
  );
};

const ClientApp = () => {
  const { clientId } = useParams();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("plan");
  const [weeklyPlan, setWeeklyPlan] = useState([]);
  const [selectedDayIndex, setSelectedDayIndex] = useState(3); // Today is index 3
  const [showConfetti, setShowConfetti] = useState(false);
  const [nutritionistName, setNutritionistName] = useState("Dra. María González");
  const [clientName, setClientName] = useState("Ana");
  const [currentStreak, setCurrentStreak] = useState(21);

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setWeeklyPlan(generateWeeklyPlan());
      setLoading(false);
    }, 800);
  }, [clientId]);

  const selectedDay = weeklyPlan[selectedDayIndex];
  
  const calculateConsumed = (meals) => {
    if (!meals) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    
    return Object.values(meals).reduce((acc, meal) => {
      if (meal.completed) {
        return {
          calories: acc.calories + meal.calories,
          protein: acc.protein + meal.protein,
          carbs: acc.carbs + meal.carbs,
          fat: acc.fat + meal.fat
        };
      }
      return acc;
    }, { calories: 0, protein: 0, carbs: 0, fat: 0 });
  };

  const toggleMealComplete = (mealKey) => {
    setWeeklyPlan(prev => {
      const newPlan = [...prev];
      const day = { ...newPlan[selectedDayIndex] };
      const meals = { ...day.meals };
      meals[mealKey] = { ...meals[mealKey], completed: !meals[mealKey].completed };
      day.meals = meals;
      newPlan[selectedDayIndex] = day;
      
      // Check if all meals completed
      const allCompleted = Object.values(meals).every(m => m.completed);
      if (allCompleted && day.isToday) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
      }
      
      return newPlan;
    });
  };

  const consumed = selectedDay ? calculateConsumed(selectedDay.meals) : { calories: 0, protein: 0, carbs: 0, fat: 0 };
  const goals = selectedDay?.dailyGoals || { calories: 1800, protein: 120, carbs: 175, fat: 60 };
  const caloriesLeft = goals.calories - consumed.calories;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F4D40] to-[#134E3E] flex items-center justify-center">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-white animate-pulse" />
          </div>
          <p className="text-white/80 font-medium">Cargando tu plan...</p>
        </motion.div>
      </div>
    );
  }

  const renderPlanTab = () => (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      {/* Day Selector */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => setSelectedDayIndex(Math.max(0, selectedDayIndex - 1))}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          
          <div className="flex gap-2">
            {weeklyPlan.map((day, index) => (
              <button
                key={index}
                onClick={() => setSelectedDayIndex(index)}
                className={`flex flex-col items-center p-2 rounded-xl transition-all ${
                  selectedDayIndex === index 
                    ? 'bg-[#0F4D40] text-white' 
                    : day.isToday 
                      ? 'bg-[#178A6B]/10 text-[#0F4D40]' 
                      : 'hover:bg-gray-50'
                }`}
              >
                <span className={`text-xs font-medium ${
                  selectedDayIndex === index ? 'text-white/70' : 'text-gray-400'
                }`}>
                  {day.dayName}
                </span>
                <span className={`text-lg font-bold ${
                  selectedDayIndex === index ? 'text-white' : 'text-gray-700'
                }`}>
                  {day.dayNumber}
                </span>
                {day.isToday && selectedDayIndex !== index && (
                  <div className="w-1.5 h-1.5 bg-[#E57A4F] rounded-full mt-0.5"></div>
                )}
              </button>
            ))}
          </div>
          
          <button 
            onClick={() => setSelectedDayIndex(Math.min(weeklyPlan.length - 1, selectedDayIndex + 1))}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Calories Overview */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <motion.p 
              key={caloriesLeft}
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              className="text-5xl font-bold text-[#0F4D40]"
            >
              {Math.max(0, caloriesLeft)}
            </motion.p>
            <p className="text-gray-500 mt-1">Calorías restantes</p>
          </div>
          <div className="relative w-24 h-24">
            <svg className="w-full h-full -rotate-90">
              <circle
                cx="48"
                cy="48"
                r="40"
                fill="none"
                stroke="#E5E7EB"
                strokeWidth="12"
              />
              <motion.circle
                cx="48"
                cy="48"
                r="40"
                fill="none"
                stroke="#0F4D40"
                strokeWidth="12"
                strokeLinecap="round"
                strokeDasharray={251}
                initial={{ strokeDashoffset: 251 }}
                animate={{ strokeDashoffset: 251 - (consumed.calories / goals.calories) * 251 }}
                transition={{ duration: 0.5 }}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <Apple className="w-6 h-6 text-[#0F4D40]" />
            </div>
          </div>
        </div>

        {/* Macros */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <CircularProgress 
            value={consumed.protein} 
            max={goals.protein} 
            color="#DC2626" 
            size={70}
            strokeWidth={6}
            label="Proteína"
            icon={Beef}
          />
          <CircularProgress 
            value={consumed.carbs} 
            max={goals.carbs} 
            color="#F59E0B" 
            size={70}
            strokeWidth={6}
            label="Carbos"
            icon={Wheat}
          />
          <CircularProgress 
            value={consumed.fat} 
            max={goals.fat} 
            color="#3B82F6" 
            size={70}
            strokeWidth={6}
            label="Grasas"
            icon={Droplet}
          />
        </div>
      </div>

      {/* Meals */}
      <div className="space-y-3">
        <h3 className="font-bold text-[#0F4D40] px-1">Comidas del día</h3>
        
        {selectedDay && Object.entries(selectedDay.meals).map(([key, meal]) => (
          <motion.div
            key={key}
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`bg-white rounded-2xl p-4 shadow-sm border transition-all ${
              meal.completed 
                ? 'border-[#178A6B] bg-[#178A6B]/5' 
                : 'border-gray-100'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-[#0F4D40]">{meal.name}</h4>
                  <span className="text-xs text-gray-400">{meal.time}</span>
                </div>
                <p className="text-sm text-gray-500 mb-2">{meal.foods.join(' • ')}</p>
                <div className="flex items-center gap-3 text-xs">
                  <span className="font-medium text-[#0F4D40]">{meal.calories} kcal</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-red-500">P: {meal.protein}g</span>
                  <span className="text-amber-500">C: {meal.carbs}g</span>
                  <span className="text-blue-500">G: {meal.fat}g</span>
                </div>
              </div>
              
              <button
                onClick={() => toggleMealComplete(key)}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  meal.completed 
                    ? 'bg-[#178A6B] text-white' 
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                }`}
              >
                <Check className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Progress Summary */}
      <div className="bg-gradient-to-r from-[#E57A4F]/10 to-[#F59E0B]/10 border border-[#E57A4F]/20 rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-[#0F4D40]">Progreso de hoy</p>
            <p className="text-sm text-gray-500">
              {Object.values(selectedDay?.meals || {}).filter(m => m.completed).length} de 4 comidas
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-[#E57A4F]" />
            <span className="text-2xl font-bold text-[#0F4D40]">{currentStreak}</span>
            <span className="text-gray-500 text-sm">días</span>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderProgressTab = () => (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      <div className="bg-gradient-to-br from-[#E57A4F]/15 to-[#F59E0B]/15 border border-[#E57A4F]/30 rounded-3xl p-6 text-center">
        <div className="flex items-center justify-center gap-4 mb-2">
          <Flame className="w-12 h-12 text-[#E57A4F]" />
          <span className="text-6xl font-bold text-[#0F4D40]">{currentStreak}</span>
        </div>
        <p className="text-gray-600 font-semibold text-lg">días consecutivos</p>
        <p className="text-sm text-gray-500 mt-1">¡Tu mejor racha! 🏆</p>
      </div>

      <div className="bg-white border border-gray-100 rounded-3xl shadow-lg p-5">
        <h3 className="font-bold text-[#0F4D40] mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Últimos 30 días
        </h3>
        <div className="grid grid-cols-10 gap-1.5">
          {Array.from({ length: 30 }, (_, i) => (
            <motion.div
              key={i}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: i * 0.02 }}
              className={`w-full aspect-square rounded-md ${
                i >= 9 ? 'bg-gradient-to-br from-[#178A6B] to-[#0F4D40]' : 'bg-gray-100'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-gray-100 rounded-2xl p-4 text-center shadow-sm">
          <p className="text-3xl font-bold text-[#0F4D40]">45</p>
          <p className="text-xs text-gray-500 mt-1">Check-ins</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-2xl p-4 text-center shadow-sm">
          <p className="text-3xl font-bold text-[#178A6B]">87%</p>
          <p className="text-xs text-gray-500 mt-1">Adherencia</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-2xl p-4 text-center shadow-sm">
          <p className="text-3xl font-bold text-[#E57A4F]">-3kg</p>
          <p className="text-xs text-gray-500 mt-1">Progreso</p>
        </div>
      </div>
    </motion.div>
  );

  const renderChatTab = () => (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      <div className="text-center mb-2">
        <h2 className="text-xl font-bold text-[#0F4D40]">Dudas Resueltas 💬</h2>
        <p className="text-gray-500 text-sm">Consultas respondidas por HabitIA</p>
      </div>

      {simulatedQA.map((qa, index) => (
        <motion.div
          key={qa.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
        >
          <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
            <span className="text-xs font-medium text-[#178A6B] bg-[#178A6B]/10 px-2 py-1 rounded-full">
              {qa.category}
            </span>
            <span className="text-xs text-gray-400">{qa.date}</span>
          </div>
          
          <div className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm">🙋</span>
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-400 mb-1">Tu pregunta</p>
                <p className="text-sm text-gray-700 font-medium">{qa.question}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 bg-[#178A6B]/5 rounded-xl p-3 ml-2">
              <div className="w-8 h-8 bg-[#0F4D40] rounded-full flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-[#178A6B] mb-1 font-medium">HabitIA</p>
                <p className="text-sm text-gray-700">{qa.answer}</p>
              </div>
            </div>
          </div>
        </motion.div>
      ))}

      <div className="bg-gray-50 rounded-2xl p-4 text-center">
        <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">
          ¿Tienes más dudas? Escríbenos por WhatsApp
        </p>
      </div>
    </motion.div>
  );

  const renderSettingsTab = () => (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <h3 className="font-bold text-[#0F4D40] mb-4">Mi Objetivo</h3>
        <p className="text-gray-700">Perder 10 kg en 3 meses</p>
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <h3 className="font-bold text-[#0F4D40] mb-4">Mis Metas Diarias</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Calorías</span>
            <span className="font-semibold text-[#0F4D40]">1,800 kcal</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Proteína</span>
            <span className="font-semibold text-red-500">120g</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Carbohidratos</span>
            <span className="font-semibold text-amber-500">175g</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Grasas</span>
            <span className="font-semibold text-blue-500">60g</span>
          </div>
        </div>
      </div>

      {/* Fitia Integration Placeholder */}
      <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-200 rounded-2xl p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <span className="text-white text-xl">🍎</span>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Fitia</h3>
              <p className="text-xs text-gray-500">Sincroniza tu actividad física</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full border-purple-300 text-purple-600 hover:bg-purple-50"
            disabled
          >
            Próximamente
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Pronto podrás conectar Fitia para sincronizar tu actividad física y ajustar tus metas automáticamente.
        </p>
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <h3 className="font-bold text-[#0F4D40] mb-2">Mi Nutricionista</h3>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-[#0F4D40] rounded-full flex items-center justify-center">
            <span className="text-white font-bold">MG</span>
          </div>
          <div>
            <p className="font-medium text-gray-900">{nutritionistName}</p>
            <p className="text-sm text-gray-500">Nutricionista certificada</p>
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24">
      {showConfetti && (
        <Confetti 
          recycle={false} 
          numberOfPieces={200}
          colors={['#178A6B', '#E57A4F', '#F59E0B', '#0F4D40']}
        />
      )}
      
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-40 shadow-sm">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#0F4D40] to-[#178A6B] rounded-xl flex items-center justify-center shadow-md">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide">HabitIA</p>
              <p className="font-bold text-[#0F4D40] text-sm">Hola, {clientName} 👋</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-gradient-to-r from-[#E57A4F]/10 to-[#F59E0B]/10 px-3 py-2 rounded-full border border-[#E57A4F]/20">
            <Flame className="w-5 h-5 text-[#E57A4F]" />
            <span className="font-bold text-[#0F4D40] text-lg">{currentStreak}</span>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-md mx-auto px-4 py-4">
        {activeTab === "plan" && renderPlanTab()}
        {activeTab === "progress" && renderProgressTab()}
        {activeTab === "chat" && renderChatTab()}
        {activeTab === "settings" && renderSettingsTab()}
      </main>

      {/* Bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 h-20 bg-white border-t border-gray-100 shadow-lg z-[99999]">
        <div className="max-w-md mx-auto grid grid-cols-4 h-full">
          {[
            { key: "plan", icon: FileText, label: "Plan" },
            { key: "progress", icon: TrendingUp, label: "Progreso" },
            { key: "chat", icon: MessageSquare, label: "Dudas" },
            { key: "settings", icon: CheckCircle, label: "Metas" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex flex-col items-center justify-center gap-1 transition-all ${
                activeTab === tab.key 
                  ? "text-[#0F4D40]" 
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              <div className={`p-2 rounded-xl transition-all ${
                activeTab === tab.key ? 'bg-[#178A6B]/10' : ''
              }`}>
                <tab.icon className={`w-5 h-5 ${activeTab === tab.key ? 'text-[#178A6B]' : ''}`} />
              </div>
              <span className={`text-xs font-medium ${activeTab === tab.key ? 'text-[#0F4D40]' : ''}`}>
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default ClientApp;
